// src/pages/admin/DisputeDetail.jsx
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminDisputesApi } from '../../api/services'
import { Card, CardHeader, CardBody, Button, StatusBadge, PageSpinner } from '../../components/common/ui'
import toast from 'react-hot-toast'

export default function DisputeDetail() {
  const { id } = useParams()
  const qc = useQueryClient()
  const [msg, setMsg] = useState('')
  const [resolution, setResolution] = useState({ favour:'buyer', resolution_notes:'', resolution_amount:'' })

  const { data, isLoading } = useQuery({ queryKey: ['admin-dispute', id], queryFn: () => adminDisputesApi.show(id) })
  const addMsg = useMutation({ mutationFn: () => adminDisputesApi.addMessage(id, { body: msg, is_internal: false }), onSuccess: () => { setMsg(''); qc.invalidateQueries(['admin-dispute', id]) } })
  const resolve = useMutation({ mutationFn: () => adminDisputesApi.resolve(id, resolution), onSuccess: () => { toast.success('Dispute resolved'); qc.invalidateQueries(['admin-dispute', id]) }, onError: e => toast.error(e.response?.data?.message || 'Error') })

  if (isLoading) return <PageSpinner />
  const dispute = data?.data?.data || {}

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-display font-bold capitalize">{dispute.type?.replace(/_/g,' ')} Dispute</h1>
        <StatusBadge status={dispute.status} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <Card><CardBody><p className="text-xs text-muted mb-1">Raised By</p><p className="font-bold text-sm">{dispute.raised_by?.name}</p><p className="text-xs text-muted">{dispute.raised_by?.role}</p></CardBody></Card>
        <Card><CardBody><p className="text-xs text-muted mb-1">Order</p><p className="font-bold text-sm">{dispute.order?.order_number}</p></CardBody></Card>
      </div>
      {dispute.description && <Card className="mb-4"><CardBody><p className="text-sm font-bold mb-1">Description</p><p className="text-sm text-gray-600">{dispute.description}</p></CardBody></Card>}
      <Card className="mb-4">
        <CardHeader><span className="font-bold text-sm">Messages</span></CardHeader>
        <CardBody>
          <div className="space-y-3 mb-3 max-h-60 overflow-y-auto">
            {dispute.messages?.map(m => (
              <div key={m.id} className={`flex flex-col ${m.sender?.role === 'admin' ? 'items-end' : 'items-start'}`}>
                <div className={`max-w-xs px-3 py-2 rounded-2xl text-sm ${m.sender?.role === 'admin' ? 'bg-gradient-brand text-white' : 'bg-surface border border-border'}`}>
                  <p>{m.body}</p>
                  <p className={`text-xs mt-0.5 ${m.sender?.role === 'admin' ? 'text-white/70' : 'text-muted'}`}>{m.sender?.name} · {new Date(m.created_at).toLocaleTimeString()}</p>
                </div>
              </div>
            ))}
            {(!dispute.messages || dispute.messages.length === 0) && <p className="text-xs text-muted text-center">No messages yet.</p>}
          </div>
          <div className="flex gap-2">
            <input className="input flex-1" placeholder="Message…" value={msg} onChange={e => setMsg(e.target.value)} />
            <Button size="sm" loading={addMsg.isPending} disabled={!msg.trim()} onClick={() => addMsg.mutate()}>Send</Button>
          </div>
        </CardBody>
      </Card>
      {!['resolved','closed'].includes(dispute.status) && (
        <Card>
          <CardHeader><span className="font-bold text-sm">Resolve</span></CardHeader>
          <CardBody className="space-y-3">
            <select className="input w-full" value={resolution.favour} onChange={e => setResolution(r => ({ ...r, favour: e.target.value }))}>
              {['buyer','supplier','split','no_action'].map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <input className="input w-full" type="number" placeholder="Refund amount (₹, if any)" value={resolution.resolution_amount} onChange={e => setResolution(r => ({ ...r, resolution_amount: e.target.value }))} />
            <textarea className="input w-full h-20" placeholder="Resolution notes *" value={resolution.resolution_notes} onChange={e => setResolution(r => ({ ...r, resolution_notes: e.target.value }))} />
            <Button loading={resolve.isPending} disabled={!resolution.resolution_notes.trim()} onClick={() => resolve.mutate()}>Resolve Dispute</Button>
          </CardBody>
        </Card>
      )}
    </div>
  )
}
